import { useEffect, useId, useRef, useState } from "react";

import styles from "./IrritantPopups.module.css";

/*
 * PRANK — the room's calm introduction, redone as the most irritating popups we
 * could think of. A joke for Leonie, not a feature.
 *
 * It only runs when the address carries `?nieuw`, so no ordinary visitor ever
 * sees it, and it stores nothing. To roll it back, delete this folder and both
 * `IrritantPopups` lines in `src/app/App.tsx` (the import is marked PRANK).
 */
const ENABLED =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("nieuw");

const COUNTDOWN_SECONDS = 5;

type Trick = "plain" | "runaway" | "countdown" | "confirm";

const POPUPS: readonly Readonly<{ text: string; trick: Trick }>[] = [
  { text: "Hé, je bent binnen! 👋", trick: "plain" },
  { text: "Waar trekt je aandacht naartoe?", trick: "runaway" },
  { text: "Ontdek de kamer op jouw tempo.", trick: "countdown" },
  { text: "Alles mag, niets moet.", trick: "confirm" },
];

const DODGE_CAPTIONS = ["Mis! 😜", "Bijna…", "Oké, oké. Nu mag het."];

/** Every popup waits a beat, just long enough to hope it was the last one. */
function pauseBefore(step: number): number {
  const reducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) return 0;
  return step === 0 ? 700 : 350;
}

export function IrritantPopups() {
  return ENABLED ? <PopupSequence /> : null;
}

function PopupSequence() {
  const [step, setStep] = useState(0);
  const [shown, setShown] = useState(false);
  const running = step < POPUPS.length;

  // The calm introduction steps aside while the popups do its job, badly.
  useEffect(() => {
    if (!running) return undefined;
    const root = document.documentElement;
    root.dataset.irritant = "";
    return () => {
      delete root.dataset.irritant;
    };
  }, [running]);

  useEffect(() => {
    if (shown) return undefined;
    const timer = window.setTimeout(() => setShown(true), pauseBefore(step));
    return () => window.clearTimeout(timer);
  }, [shown, step]);

  if (!shown) return null;

  const popup = POPUPS[step];
  if (!popup) return <Punchline />;

  return (
    <Popup
      key={step}
      number={step + 1}
      text={popup.text}
      trick={popup.trick}
      onDone={() => {
        setShown(false);
        setStep(step + 1);
      }}
    />
  );
}

type PopupProps = Readonly<{
  number: number;
  text: string;
  trick: Trick;
  onDone: () => void;
}>;

function Popup({ number, text, trick, onDone }: PopupProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepOpenRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();
  const [confirming, setConfirming] = useState(false);
  const [shakes, setShakes] = useState(0);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  useEffect(() => {
    if (confirming) keepOpenRef.current?.focus();
  }, [confirming]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.popup}
      data-spot={number}
      data-shake={shakes === 0 ? undefined : shakes % 2 === 0 ? "even" : "odd"}
      aria-labelledby={labelId}
      onCancel={(event) => {
        // Escape only makes it wobble. Chrome lets a second press close it
        // regardless, and that simply counts as closing it.
        event.preventDefault();
        setShakes(shakes + 1);
      }}
      onClose={onDone}
    >
      <p className={styles.titleBar}>
        ⚠️ Melding {confirming ? number + 1 : number} van {POPUPS.length}
      </p>

      {confirming ? (
        <div className={styles.body}>
          <p id={labelId} className={styles.text}>
            Weet je zeker dat je dit wilt sluiten?
          </p>
          <p className={styles.smallPrint}>
            Alles mag, niets moet. Behalve dit even bevestigen.
          </p>
          <div className={styles.choices}>
            <button
              ref={keepOpenRef}
              className={styles.keepOpen}
              type="button"
              onClick={() => setConfirming(false)}
            >
              Nee, laat maar open
            </button>
            <button className={styles.sneakyYes} type="button" onClick={onDone}>
              ja
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.body}>
          <p id={labelId} className={styles.text}>
            {text}
          </p>
          {trick === "plain" ? <CloseButton onClick={onDone} /> : null}
          {trick === "runaway" ? <RunawayClose onClick={onDone} /> : null}
          {trick === "countdown" ? <CountdownClose onClick={onDone} /> : null}
          {trick === "confirm" ? (
            <CloseButton onClick={() => setConfirming(true)} />
          ) : null}
        </div>
      )}
    </dialog>
  );
}

type CloseProps = Readonly<{ onClick: () => void }>;

function CloseButton({ onClick }: CloseProps) {
  return (
    <button className={styles.close} type="button" aria-label="Sluiten" onClick={onClick}>
      ✕
    </button>
  );
}

/** Jumps away from the mouse a few times before it lets itself be caught. */
function RunawayClose({ onClick }: CloseProps) {
  const [dodges, setDodges] = useState(0);

  return (
    <>
      {dodges > 0 ? <p className={styles.caption}>{DODGE_CAPTIONS[dodges - 1]}</p> : null}
      <button
        className={styles.close}
        data-spot={dodges}
        type="button"
        aria-label="Sluiten"
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse" && dodges < DODGE_CAPTIONS.length) {
            setDodges(dodges + 1);
          }
        }}
        onClick={onClick}
      >
        ✕
      </button>
    </>
  );
}

/** "Op jouw tempo" — as long as that tempo is ours. Clicking early starts over. */
function CountdownClose({ onClick }: CloseProps) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [scolded, setScolded] = useState(false);
  const ready = secondsLeft === 0;

  useEffect(() => {
    if (ready) return undefined;
    const timer = window.setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [ready, secondsLeft]);

  return (
    <>
      {scolded ? (
        <p className={styles.caption}>Rustig aan, op jóúw tempo zeiden we. Opnieuw. 😌</p>
      ) : null}
      <button
        className={styles.countdown}
        type="button"
        aria-disabled={!ready}
        onClick={() => {
          if (ready) {
            onClick();
            return;
          }
          setScolded(true);
          setSecondsLeft(COUNTDOWN_SECONDS);
        }}
      >
        {ready ? "Sluiten ✕" : `Sluiten kan over ${secondsLeft}…`}
      </button>
    </>
  );
}

function Punchline() {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setOpen(false), 6000);
    return () => window.clearTimeout(timer);
  }, []);

  if (!open) return null;

  return (
    <div className={styles.punchline} role="status">
      <p>Grapje. 🌊 Welkom in de Soul Surf Shack.</p>
      <button type="button" aria-label="Sluiten" onClick={() => setOpen(false)}>
        ✕
      </button>
    </div>
  );
}
